import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Trees, Heart, Euro } from "lucide-react";

interface SocialProject {
  id: string;
  name: string;
  project_type: string;
}

interface ProjectCost {
  id?: string;
  social_project_id: string;
  cost_per_unit: number;
}

interface AdminSocialCostsProps {
  tenantId: string;
  availableProjects: SocialProject[];
}

export function AdminSocialCosts({ tenantId, availableProjects }: AdminSocialCostsProps) {
  const [projectCosts, setProjectCosts] = useState<ProjectCost[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (tenantId) {
      fetchProjectCosts();
    }
  }, [tenantId]);

  const fetchProjectCosts = async () => {
    try {
      const { data, error } = await supabase
        .from('social_project_costs')
        .select('*')
        .eq('tenant_id', tenantId);

      if (error) throw error;

      setProjectCosts(data || []);
    } catch (error) {
      console.error('Error fetching project costs:', error);
      toast({
        title: "Fehler beim Laden",
        description: "Kosten konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getProjectCost = (projectId: string): number => {
    const cost = projectCosts.find(c => c.social_project_id === projectId);
    return cost?.cost_per_unit || 10.00;
  };

  const updateProjectCost = (projectId: string, cost: number) => {
    const updatedCosts = [...projectCosts];
    const existingIndex = updatedCosts.findIndex(c => c.social_project_id === projectId);
    
    if (existingIndex >= 0) {
      updatedCosts[existingIndex].cost_per_unit = cost;
    } else {
      updatedCosts.push({ social_project_id: projectId, cost_per_unit: cost });
    }
    
    setProjectCosts(updatedCosts);
  };

  const saveProjectCosts = async () => {
    setSaving(true);
    try {
      // Upsert all project costs
      for (const cost of projectCosts) {
        const { error } = await supabase
          .from('social_project_costs')
          .upsert({
            tenant_id: tenantId,
            social_project_id: cost.social_project_id,
            cost_per_unit: cost.cost_per_unit,
          }, {
            onConflict: 'tenant_id,social_project_id'
          });

        if (error) throw error;
      }

      toast({
        title: "Kosten gespeichert",
        description: "Die Projekt-Kosten wurden erfolgreich aktualisiert.",
      });
    } catch (error) {
      console.error('Error saving project costs:', error);
      toast({
        title: "Fehler beim Speichern",
        description: "Die Kosten konnten nicht gespeichert werden.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const getProjectIcon = (projectType: string) => {
    switch (projectType) {
      case 'trees':
        return <Trees className="h-5 w-5 text-green-600" />;
      case 'families':
      case 'family_help':
        return <Heart className="h-5 w-5 text-red-600" />;
      default:
        return <Euro className="h-5 w-5 text-blue-600" />;
    }
  };

  const getProjectTypeName = (projectType: string) => {
    switch (projectType) {
      case 'trees':
        return 'Baum-Projekt';
      case 'families':
      case 'family_help':
        return 'Familien-Unterstützung';
      default:
        return 'Soziales Projekt';
    }
  };

  if (loading) {
    return <div className="p-4">Lade Projekt-Kosten...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Euro className="h-5 w-5" />
          Kosten pro Einheit festlegen
        </CardTitle>
        <CardDescription>
          Bestimmen Sie, wie viel Sie pro Baum oder Familie investieren möchten.
          Diese Kosten werden verwendet, um Ihre Verpflichtungen zu berechnen.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {availableProjects.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            Keine aktiven sozialen Projekte verfügbar.
          </p>
        ) : (
          <div className="space-y-4">
            {availableProjects.map((project) => (
              <div key={project.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  {getProjectIcon(project.project_type)}
                  <div>
                    <h4 className="font-medium">{project.name}</h4>
                    <p className="text-sm text-muted-foreground">
                      {getProjectTypeName(project.project_type)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Label htmlFor={`cost-${project.id}`} className="text-sm">
                    Kosten pro Einheit:
                  </Label>
                  <div className="flex items-center gap-1">
                    <Input
                      id={`cost-${project.id}`}
                      type="number"
                      min="0"
                      step="0.01"
                      value={getProjectCost(project.id)}
                      onChange={(e) => updateProjectCost(project.id, parseFloat(e.target.value) || 0)}
                      className="w-20 text-right"
                    />
                    <span className="text-sm text-muted-foreground">€</span>
                  </div>
                </div>
              </div>
            ))}
            
            <Separator />
            
            <div className="flex justify-end">
              <Button onClick={saveProjectCosts} disabled={saving}>
                {saving ? "Speichere..." : "Kosten speichern"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}